import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import ArrowUpRightIcon from "@redotech/redo-icons/arrow-up-right_filled.svg";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { REDO_MERCHANT_ONBOARDING_URL } from "@redotech/redo-merchant-app-common/config";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useEffect, useState } from "react";
import { getCurrentOnboarding, onboardingTc } from "../client/onboarding";
import * as indexCss from "./index.module.css";

const tcUrl = new URL("https://www.getredo.com/terms-conditions");
const TC_IMAGE_URL =
  "https://data.getredo.com/redo/static/onboarding/t-c-accept.webp";
const setUpOnboardingUrl = new URL(
  "https://df6ln.share.hsforms.com/2HfPRu66ySwWl0ScYAnAl7Q",
);

export function autoOnboardingUrl(token: string, teamId: string) {
  const url = new URL(REDO_MERCHANT_ONBOARDING_URL);
  url.searchParams.set("token", token);
  url.searchParams.set("teamId", teamId);
  return url;
}

export const Onboarding = memo(function Onboarding() {
  const team = useContext(TeamContext);
  const auth = useContext(AuthContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const [showModal, setShowModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true);

  const onboardingFeatureFlag =
    team?.featureFlags?.[FeatureFlagKey.MERCHANT_ONBOARDING];

  const [acceptTermsLoad, doAcceptTerms] = useTriggerLoad(async (signal) => {
    return alertOnFailure("Failed to onboard")(async () => {
      await onboardingTc(client, { signal });
      if (onboardingFeatureFlag === "guided" && auth) {
        location.href = autoOnboardingUrl(auth.token, auth.teamId).toString();
      }
      return true;
    });
  });

  const onboardingLoad = useLoad(async (signal) => {
    const data = await getCurrentOnboarding(client, { signal });
    if (data.tcConfirmed) {
      setShowModal(false);
      return data;
    }
    // Retry once after a short delay to guard against read replica lag
    // returning a stale tcConfirmed=false for merchants who already accepted
    await new Promise((r) => setTimeout(r, 1500));
    if (signal.aborted) return data;
    const retry = await getCurrentOnboarding(client, { signal });
    setShowModal(!retry.tcConfirmed);
    return retry;
  }, []);

  useEffect(() => {
    const isShopify =
      team?.name.toLowerCase().includes("shpfy test") ||
      team?.email.endsWith("@shopify.com");
    if (acceptTermsLoad.value && isShopify) {
      setShowModal(false);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptTermsLoad]);

  if (!showModal) {
    return null;
  }

  if (acceptTermsLoad.pending) {
    return (
      <RedoModal
        hideCloseButton
        isOpen
        modalSize="sm"
        onModalCloseRequested={() => {}}
        primaryButton={{
          text: "Setting up...",
          onClick: () => {},
          pending: true,
        }}
        title="Setting up Redo"
      />
    );
  }

  if (acceptTermsLoad.value) {
    return (
      <RedoModal
        hideCloseButton
        hideFooter
        hideHeader
        isOpen
        modalSize="sm"
        noPadding
        onModalCloseRequested={() => setShowModal(false)}
        title=""
      >
        <Flex
          className={indexCss.fullContent}
          dir="column"
          justify="space-between"
        >
          <Flex dir="column" gap="xs">
            <Text fontSize="xl" fontWeight="semibold" textColor="primary">
              Welcome to Redo
            </Text>
            <Text fontSize="sm" textColor="tertiary">
              Schedule an appointment with our onboarding specialists to finish
              setting up.
            </Text>
          </Flex>
          <Flex align="flex-start" dir="column" gap="md">
            <a
              className={indexCss.link}
              href={setUpOnboardingUrl.toString()}
              rel="noreferrer"
              target="_blank"
            >
              <Text fontSize="sm" textColor="primary">
                Schedule an appointment
              </Text>
              <ArrowUpRightIcon className={indexCss.linkIcon} />
            </a>
            <RedoButton
              className={indexCss.acceptButton}
              hierarchy="primary"
              onClick={() => setShowModal(false)}
              size="md"
              text="Continue to Redo"
              theme="normal"
            />
          </Flex>
        </Flex>
      </RedoModal>
    );
  }

  if (acceptTermsLoad.error || onboardingLoad.error) {
    return (
      <RedoModal
        hideCloseButton
        isOpen
        modalSize="sm"
        onModalCloseRequested={() => {}}
        title="Welcome to Redo"
      >
        <Text fontSize="sm" textColor="tertiary">
          There was an error during onboarding.
        </Text>
      </RedoModal>
    );
  }

  return (
    <RedoModal
      hideCloseButton
      hideFooter
      hideHeader
      isOpen
      modalSize="md"
      noPadding
      onModalCloseRequested={() => {}}
      title=""
    >
      <Flex className={indexCss.splitLayout} dir="row">
        {imageLoaded && (
          <div className={indexCss.sideImageWrapper}>
            <img
              alt="Welcome to Redo"
              className={indexCss.sideImage}
              onError={() => setImageLoaded(false)}
              src={TC_IMAGE_URL}
            />
          </div>
        )}
        <Flex
          className={imageLoaded ? indexCss.sideContent : indexCss.fullContent}
          dir="column"
          justify="space-between"
        >
          <Flex dir="column" gap="xs">
            <Text fontSize="xl" fontWeight="semibold" textColor="primary">
              Welcome to Redo
            </Text>
            <Text fontSize="sm" textColor="tertiary">
              Please review and accept the terms and conditions to continue.
            </Text>
          </Flex>
          <Flex align="flex-start" dir="column" gap="md">
            <a
              className={indexCss.link}
              href={tcUrl.toString()}
              rel="noreferrer"
              target="_blank"
            >
              <Text fontSize="sm" textColor="primary">
                Read terms and conditions
              </Text>
              <ArrowUpRightIcon className={indexCss.linkIcon} />
            </a>
            <RedoButton
              className={indexCss.acceptButton}
              hierarchy="primary"
              onClick={() => doAcceptTerms()}
              size="md"
              text="Accept"
              theme="normal"
            />
          </Flex>
        </Flex>
      </Flex>
    </RedoModal>
  );
});
